import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { apiError } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import Select from '../../components/ui/Select';
import CountrySelect, { CodeChip } from '../../components/ui/CountrySelect';
import { Card, Field, FormActions, LoadingRows, Notice, PageHeader, Toggle } from '../../components/ui/Page';
import { COUNTRY_CURRENCY, countryName } from '../../lib/countries';
import { moneyMajor } from '../../lib/format';
import { DEFAULT_CODE, scheduleSlug, type FeesResponse } from './index';

const CURRENCY_LABEL: Record<string, string> = {
  INR: 'Indian rupee',
  USD: 'US dollar',
  EUR: 'Euro',
  GBP: 'British pound',
  SGD: 'Singapore dollar',
  AED: 'UAE dirham',
  AUD: 'Australian dollar',
  CAD: 'Canadian dollar',
};

export default function FeeFormPage() {
  const params = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const slug = params.code || search.get('country') || '';
  const fromSlug = slug === 'default' ? DEFAULT_CODE : slug.toUpperCase();
  const editing = Boolean(params.code);
  const { data, loading } = useApi<FeesResponse>('/admin/fees');
  const schedules = useMemo(() => data?.schedules || [], [data]);
  const existing = schedules.find((s) => s.countryCode === fromSlug);

  const [form, setForm] = useState({
    countryCode: fromSlug || '',
    countryName: '',
    currency: 'USD',
    ratingFee: '',
    successFeePercent: '100',
    minSuccessFee: '',
    taxName: 'Tax',
    taxPercent: '0',
    taxInclusive: false,
    taxNumber: '',
    serviceCode: '',
    taxNote: '',
    isActive: true,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pickCountry = useCallback(
    (code: string, name: string) => {
      const found = schedules.find((s) => s.countryCode === code);
      if (found) {
        navigate(`/fees/${scheduleSlug(code)}`, { replace: true });
        return;
      }
      const currency = code === DEFAULT_CODE ? 'USD' : COUNTRY_CURRENCY[code] || 'USD';
      const tax = data?.taxDefaults[code] || { taxName: 'Tax', taxPercent: 0 };
      setForm((f) => ({
        ...f,
        countryCode: code,
        countryName: code === DEFAULT_CODE ? 'All other countries' : name,
        currency,
        ratingFee: f.ratingFee || (currency === 'INR' ? '5000' : '75'),
        taxName: tax.taxName,
        taxPercent: String(tax.taxPercent),
        taxNote: tax.taxNote || '',
      }));
    },
    [schedules, data, navigate],
  );

  useEffect(() => {
    if (existing) {
      setForm({
        countryCode: existing.countryCode,
        countryName: existing.countryName,
        currency: existing.currency,
        ratingFee: String(existing.ratingFee / 100),
        successFeePercent: String(existing.successFeePercent),
        minSuccessFee: existing.minSuccessFee ? String(existing.minSuccessFee / 100) : '',
        taxName: existing.taxName,
        taxPercent: String(existing.taxPercent),
        taxInclusive: existing.taxInclusive,
        taxNumber: existing.taxNumber || '',
        serviceCode: existing.serviceCode || '',
        taxNote: existing.taxNote || '',
        isActive: existing.isActive,
        notes: existing.notes || '',
      });
    } else if (data && !editing && fromSlug && (fromSlug === DEFAULT_CODE || /^[A-Z]{2}$/.test(fromSlug))) {
      pickCountry(fromSlug, countryName(fromSlug));
    }
  }, [existing, fromSlug, data, editing, pickCountry]);

  const configured = useMemo(() => Object.fromEntries(schedules.map((s) => [s.countryCode, 'Already priced — opens the existing schedule'])), [schedules]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.countryCode) {
      setError('Choose a country.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put('/admin/fees', {
        ...form,
        ratingFee: Number(form.ratingFee || 0),
        successFeePercent: Number(form.successFeePercent || 0),
        minSuccessFee: Number(form.minSuccessFee || 0),
        taxPercent: Number(form.taxPercent || 0),
        taxNumber: form.taxNumber || null,
        serviceCode: form.serviceCode || null,
        taxNote: form.taxNote || null,
        notes: form.notes || null,
      });
      navigate('/fees', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not save pricing.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existing || !window.confirm(`Delete pricing for ${existing.countryCode === DEFAULT_CODE ? 'the international default' : existing.countryName}?`)) return;
    try {
      await api.delete(`/admin/fees/${existing.id}`);
      navigate('/fees', { replace: true });
    } catch (err) {
      setError(apiError(err, 'Could not delete.'));
    }
  };

  if (loading && !data) return <LoadingRows rows={6} />;
  if (editing && !existing) return <Notice tone="error">No pricing exists for {countryName(fromSlug)}.</Notice>;

  const rating = Number(form.ratingFee || 0);
  const pct = Number(form.successFeePercent || 0);
  const min = Number(form.minSuccessFee || 0);
  const taxPct = Number(form.taxPercent || 0);
  const exampleRaise = form.currency === 'INR' ? 20000000 : 1000000;
  const exampleSuccess = Math.max((exampleRaise * pct) / 100, min);
  const title = form.countryCode === DEFAULT_CODE ? 'International default' : form.countryName || 'New country';
  const currencies = data?.currencies || ['INR', 'USD'];

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Fees', to: '/fees' }, { label: editing ? title : 'New pricing' }]}
        title={editing ? `Pricing · ${title}` : 'Add country pricing'}
        description="Unpaid processing fees update to the new amount on the company's next visit."
        actions={
          existing && (
            <button type="button" onClick={() => void remove()} className="mz-btn-danger">
              Delete
            </button>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <form onSubmit={onSubmit} className="grid gap-4 sm:grid-cols-2">
            <Field label="Country" htmlFor="f-country" className="sm:col-span-2" hint={editing ? 'The country of an existing schedule cannot change.' : 'Countries without their own pricing use the international default.'}>
              <CountrySelect
                id="f-country"
                value={form.countryCode}
                disabled={editing}
                onChange={pickCountry}
                highlight={configured}
                extraOptions={[{ value: DEFAULT_CODE, label: 'International default', description: configured[DEFAULT_CODE] || 'Every country without its own pricing', prefix: <CodeChip code="★" /> }]}
              />
            </Field>
            <Field label="Currency" htmlFor="f-currency">
              <Select
                id="f-currency"
                value={form.currency}
                onChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                options={currencies.map((c) => ({ value: c, label: c, description: CURRENCY_LABEL[c] }))}
              />
            </Field>
            <Field label="Processing (rating) fee" htmlFor="f-fee" hint="One-time. 0 makes the rating free.">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#a77d92]">{form.currency}</span>
                <input id="f-fee" required type="number" min="0" step="0.01" className="mz-input pl-12" value={form.ratingFee} onChange={(e) => setForm((f) => ({ ...f, ratingFee: e.target.value }))} />
              </div>
            </Field>
            <Field label={`Success fee · ${pct || 0}% of total funded amount`} htmlFor="f-pct" className="sm:col-span-2">
              <div className="flex items-center gap-3">
                <input type="range" className="mz-range flex-1" min={0} max={100} step={1} value={pct} onChange={(e) => setForm((f) => ({ ...f, successFeePercent: e.target.value }))} aria-label="Success fee percent" />
                <div className="relative w-24">
                  <input id="f-pct" required type="number" min="0" max="100" step="0.01" className="mz-input pr-7" value={form.successFeePercent} onChange={(e) => setForm((f) => ({ ...f, successFeePercent: e.target.value }))} />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#a77d92]">%</span>
                </div>
              </div>
            </Field>
            <Field label="Minimum success fee" htmlFor="f-min" hint="Optional floor for small raises.">
              <input id="f-min" type="number" min="0" step="0.01" className="mz-input" value={form.minSuccessFee} onChange={(e) => setForm((f) => ({ ...f, minSuccessFee: e.target.value }))} placeholder="0" />
            </Field>
            <Field label="Display name" htmlFor="f-name" hint="Shown on invoices and the website.">
              <input id="f-name" required minLength={2} className="mz-input" value={form.countryName} onChange={(e) => setForm((f) => ({ ...f, countryName: e.target.value }))} />
            </Field>
            <Field label="Notes" htmlFor="f-notes" className="sm:col-span-2" hint="Shown under the price on micusify.com, e.g. “Inclusive of GST”.">
              <input id="f-notes" className="mz-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} maxLength={500} />
            </Field>

            <div className="border-t border-[#fbeef4] pt-4 sm:col-span-2">
              <p className="text-sm font-bold text-[#3b0a22]">Tax</p>
              <p className="mt-0.5 text-xs text-[#a77d92]">
                Applied to every rating and success fee invoice for companies in {title}. Pre-filled with the standard rate — only charge it where Micus is registered; otherwise use 0% and a statutory note.
              </p>
            </div>
            <Field label="Tax name" htmlFor="f-taxname" hint="Printed on invoices, e.g. GST, VAT, Sales tax.">
              <input id="f-taxname" required minLength={2} maxLength={40} className="mz-input" value={form.taxName} onChange={(e) => setForm((f) => ({ ...f, taxName: e.target.value }))} />
            </Field>
            <Field label="Tax rate" htmlFor="f-taxpct" hint={form.countryCode === 'IN' ? 'Split into CGST + SGST or IGST on the invoice by the company’s state.' : '0 for no tax or zero-rated exports.'}>
              <div className="relative">
                <input id="f-taxpct" required type="number" min="0" max="50" step="0.01" className="mz-input pr-7" value={form.taxPercent} onChange={(e) => setForm((f) => ({ ...f, taxPercent: e.target.value }))} />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#a77d92]">%</span>
              </div>
            </Field>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-[#fdf6f9] px-3 py-2.5 sm:col-span-2">
              <div>
                <p className="text-[13px] font-semibold">Fees include tax</p>
                <p className="text-xs text-[#a77d92]">{form.taxInclusive ? 'The fee above is the total the company pays; tax is carved out of it.' : 'Tax is added on top of the fee above.'}</p>
              </div>
              <Toggle checked={form.taxInclusive} onChange={(v) => setForm((f) => ({ ...f, taxInclusive: v }))} label="Fees include tax" />
            </div>
            <Field label="Micus tax registration" htmlFor="f-taxno" hint="GSTIN, VAT or TRN number — printed on invoices.">
              <input id="f-taxno" maxLength={40} className="mz-input font-mono text-[13px]" value={form.taxNumber} onChange={(e) => setForm((f) => ({ ...f, taxNumber: e.target.value.toUpperCase() }))} placeholder={form.countryCode === 'IN' ? '29ABCDE1234F1Z5' : 'Optional'} />
            </Field>
            <Field label="Service code" htmlFor="f-sac" hint={form.countryCode === 'IN' ? 'SAC code of the service.' : 'Optional classification code.'}>
              <input id="f-sac" maxLength={40} className="mz-input font-mono text-[13px]" value={form.serviceCode} onChange={(e) => setForm((f) => ({ ...f, serviceCode: e.target.value }))} placeholder="Optional" />
            </Field>
            <Field label="Invoice tax note" htmlFor="f-taxnote" className="sm:col-span-2" hint="Statutory wording, e.g. “Reverse charge applies” or “Export of services under LUT without payment of IGST”.">
              <input id="f-taxnote" maxLength={300} className="mz-input" value={form.taxNote} onChange={(e) => setForm((f) => ({ ...f, taxNote: e.target.value }))} />
            </Field>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-[#fdf6f9] px-3 py-2.5 sm:col-span-2">
              <div>
                <p className="text-[13px] font-semibold">Live</p>
                <p className="text-xs text-[#a77d92]">Inactive pricing falls back to the international default.</p>
              </div>
              <Toggle checked={form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} label="Live" />
            </div>
            {error && (
              <Notice tone="error" className="sm:col-span-2">
                {error}
              </Notice>
            )}
            <div className="sm:col-span-2">
              <FormActions>
                <button type="button" className="mz-btn-ghost" onClick={() => navigate('/fees')}>
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="mz-btn-primary">
                  {saving ? 'Saving…' : 'Save pricing'}
                </button>
              </FormActions>
            </div>
          </form>
        </Card>

        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="overflow-hidden rounded-2xl text-white" style={{ background: 'linear-gradient(145deg, #2b0018 0%, #7a0038 50%, #d6006e 100%)', boxShadow: '0 18px 44px rgba(157,0,80,0.25)' }}>
            <div className="p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">Customer sees · {title}</p>
              <p className="mt-3 text-4xl font-extrabold tracking-tight">{rating ? moneyMajor(rating, form.currency) : 'Free'}</p>
              <p className="mt-1 text-xs text-white/70">
                One-time credit rating processing fee
                {taxPct > 0 && (form.taxInclusive ? ` · incl. ${form.taxName} ${taxPct}%` : ` + ${form.taxName} ${taxPct}% = ${moneyMajor(rating * (1 + taxPct / 100), form.currency)}`)}
              </p>
              <div className="my-4 h-px bg-white/20" />
              <p className="text-2xl font-extrabold">{pct || 0}%</p>
              <p className="text-xs text-white/70">of the total funded amount{min ? ` (min ${moneyMajor(min, form.currency)})` : ''}</p>
              {form.notes && <p className="mt-3 text-[11px] text-white/60">{form.notes}</p>}
            </div>
            <div className="bg-black/20 px-5 py-3 text-xs text-white/80">
              On a {moneyMajor(exampleRaise, form.currency, { compact: true })} raise Micus earns{' '}
              <strong className="text-white">{moneyMajor(rating + exampleSuccess, form.currency)}</strong>
            </div>
          </div>
          <p className="px-1 text-xs leading-relaxed text-[#a77d92]">
            {form.countryCode === 'IN'
              ? 'India: Razorpay standard checkout — UPI, cards, netbanking and wallets.'
              : 'Outside India: Razorpay international cards. Enable International Payments on the Razorpay account.'}
          </p>
        </div>
      </div>
    </div>
  );
}
